// Minimal button primitive. Two variants: "primary" (indigo accent) and
// "secondary" (slate outline). Includes a disabled state and an optional
// loading indicator. Forward all native <button> props for flexibility.

import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "success";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  children: ReactNode;
}

const base =
  "inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium " +
  "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
  "focus-visible:ring-indigo-600 disabled:cursor-not-allowed disabled:opacity-50";

const variants: Record<Variant, string> = {
  primary: "bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800",
  secondary:
    "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100",
  success: "bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800",
};

export default function Button({
  variant = "primary",
  loading = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  const cls = `${base} ${variants[variant]} ${className ?? ""}`.trim();
  return (
    <button {...rest} type={type} disabled={disabled || loading} className={cls}>
      {loading ? "..." : children}
    </button>
  );
}
